import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import type { AppLanguage, RegisterOptions, ResourceBundle } from './types';

let initialized = false;

/**
 * 初始化 i18n（在应用启动时调用）
 */
export function setupI18n(initialLanguage: AppLanguage = 'ja') {
  if (initialized) return i18next;

  i18next.use(initReactI18next).init({
    lng: initialLanguage,
    fallbackLng: 'ja',
    // 仅内存资源，不使用远程加载
    resources: {},
    interpolation: {
      escapeValue: false,
    },
    // 为稳定性关闭调试
    debug: false,
    // 不等待后台加载（我们手动注册资源）
    react: {
      useSuspense: false,
    },
  });

  initialized = true;
  if (typeof document !== 'undefined') {
    try {
      document.documentElement.setAttribute('lang', initialLanguage);
      document.documentElement.setAttribute('data-lang', initialLanguage);
    } catch (e) {
      // no-op
    }
  }
  return i18next;
}

/**
 * 为指定命名空间注册多语言资源
 */
export function registerResources({ ns, resources }: RegisterOptions) {
  const langs = Object.keys(resources) as AppLanguage[];
  langs.forEach((lang) => {
    const bundle: ResourceBundle = resources[lang];
    if (!bundle) return;
    // 覆盖并合并现有资源
    i18next.addResourceBundle(lang, ns, bundle, true, true);
  });
}

/** 切换语言 */
export function setLanguage(lang: AppLanguage) {
  if (!initialized) setupI18n(lang);
  i18next.changeLanguage(lang);
  if (typeof document !== 'undefined') {
    try {
      document.documentElement.setAttribute('lang', lang);
      document.documentElement.setAttribute('data-lang', lang);
    } catch (e) {
      // no-op
    }
  }
}

/** 读取当前语言 */
export function getLanguage(): AppLanguage {
  const current = (i18next.language || 'ja') as AppLanguage;
  return current;
}

/**
 * 同步语言（从原生/桥接层获取），避免 shared 直接依赖 app 层
 */
export function syncLanguage(fetcher: () => AppLanguage) {
  try {
    const lang = fetcher();
    setLanguage(lang);
  } catch {
    // 出错时保持默认
  }
}

export default i18next;
